import { useCallback, useEffect, useState } from "react";
import type { UseToastOptions } from "@chakra-ui/react";
import { DocumentType, gql } from "__generated__/gql";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import usePollForIds, { PollFetchContext } from "utils/usePollForIds";
import { assert, assertPresence } from "utils/assertions";

const query = gql(/* GraphQL */ `
  query PollForwardedEmailMessageByLambdaRequestId($lambdaRequestId: UUID!) {
    forwardedEmailMessageByLambdaRequestId(lambdaRequestId: $lambdaRequestId) {
      emailMessageId
      createdAt
      lambdaRequestId
    }
  }
`);

type ForwardedEmailMessage = NonNullable<
  DocumentType<typeof query>["forwardedEmailMessageByLambdaRequestId"]
>;

type UsePollForForwardedEmailMessagesOptions = {
  errorToastOptions?: UseToastOptions;
  maxTries?: number | null;
  onCompleted?: (message: ForwardedEmailMessage) => void;
  pollInterval?: number;
  timeoutMs?: number;
};

type UsePollForForwardedEmailMessagesReturn = {
  pendingRequestId: UUID | null;
  setPendingRequestId: (requestId: UUID) => void;
};

export default function usePollForForwardedEmailMessages({
  onCompleted,
  pollInterval = 5000,
  maxTries = 24,
  errorToastOptions = {
    title: "An error occurred checking forward status",
  },
  timeoutMs = 2 * 60 * 1000,
}: UsePollForForwardedEmailMessagesOptions): UsePollForForwardedEmailMessagesReturn {
  const [pendingRequestId, setPendingRequestIdState] = useState<UUID | null>(
    null,
  );
  const [timeoutAt, setTimeoutAt] = useState<number | null>(null);
  const { showErrorToast } = useAsyncCompleteToast();

  const setPendingRequestId = useCallback(
    (requestId: UUID) => {
      setPendingRequestIdState(requestId);
      setTimeoutAt(Date.now() + timeoutMs);
    },
    [timeoutMs],
  );

  const performPollFetch = useCallback(
    async (ids: UUID[], context: PollFetchContext) => {
      assert(ids.length === 1, "Expected exactly one pending request ID");
      const { data } = await context.client.query({
        query,
        variables: { lambdaRequestId: ids[0] },
        fetchPolicy: "network-only",
      });
      const message = data.forwardedEmailMessageByLambdaRequestId;
      if (message === null) return;
      assertPresence(
        message?.lambdaRequestId,
        "Expected message to have lambdaRequestId",
      );
      if (message.lambdaRequestId === pendingRequestId) {
        setPendingRequestIdState(null);
        setTimeoutAt(null);
      }
      onCompleted?.(message);
    },
    [onCompleted, pendingRequestId],
  );

  usePollForIds({
    ids: pendingRequestId ? [pendingRequestId] : [],
    performPollFetch,
    pollInterval,
    maxTries,
    errorToastOptions,
  });

  useEffect(() => {
    if (!pendingRequestId || !timeoutAt) return;
    const actualTimeoutMs = Math.max(timeoutAt - Date.now(), 0);
    const timeoutId = setTimeout(() => {
      setPendingRequestIdState(null);
      setTimeoutAt(null);
      showErrorToast(undefined, {
        title: "Email forwarding request timed out",
      });
    }, actualTimeoutMs);
    return () => clearTimeout(timeoutId);
  }, [pendingRequestId, showErrorToast, timeoutAt]);

  return {
    pendingRequestId,
    setPendingRequestId,
  };
}
